'use client';

/**
 * Shared motion primitives for the Ultragoon user app.
 *
 * Everything here is built on framer-motion and is reduced-motion-aware:
 * when the user has `prefers-reduced-motion: reduce` set, all transform/opacity
 * animation is disabled (components render their final, static state). This guard
 * is baked into the primitives so every consumer honors the preference for free —
 * page agents should NOT re-implement reduced-motion handling.
 *
 * Motion design tokens (keep usage CLEAN + SUBTLE):
 *   durations: fast 0.15s, base 0.22s, slow 0.4s
 *   easeOut for enters: [0.16, 1, 0.3, 1]
 *   page transition: fade + 8px upward slide, base duration
 *   list/grid stagger: container staggerChildren ~0.04s; child fade + 8px up
 *   hover: cards lift y -2..-3px + soft shadow + ~1.01-1.02 scale
 *   buttons: hover scale ~1.02, tap scale ~0.97 (light spring)
 */

import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import {
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type Transition,
  type Variants,
} from 'framer-motion';

/* ──────────────────────────────────────────────────────────────────────────
 * Tokens
 * ────────────────────────────────────────────────────────────────────────── */

export const DURATION = {
  fast: 0.15,
  base: 0.22,
  slow: 0.4,
} as const;

/** Custom easeOut curve for enter animations. */
export const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** Light spring for hover/tap interactions (buttons, pressables). */
export const TAP_SPRING: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 28,
  mass: 0.6,
};

/** Vertical travel (px) for fade+slide enters. */
const SLIDE_Y = 8;

/* ──────────────────────────────────────────────────────────────────────────
 * Reduced-motion guard
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Returns whether motion should be ACTIVE. `false` when the user prefers reduced
 * motion (so callers can no-op transforms/animation). Exposed for the rare case a
 * consumer needs to branch manually — most code should just use the primitives.
 */
export function useMotionEnabled(): boolean {
  // useReducedMotion returns null until hydrated; treat null as "no preference".
  return !useReducedMotion();
}

/* ──────────────────────────────────────────────────────────────────────────
 * FadeIn — fade + slide-up on mount
 * ────────────────────────────────────────────────────────────────────────── */

export interface FadeInProps extends HTMLMotionProps<'div'> {
  /** Stagger delay in seconds before this element animates in. */
  delay?: number;
  /** Animation duration in seconds. Defaults to base (0.22s). */
  duration?: number;
  children?: ReactNode;
}

/**
 * Fades + slides its children up 8px on mount.
 * No-ops (renders final state) under prefers-reduced-motion.
 */
export const FadeIn = forwardRef<HTMLDivElement, FadeInProps>(function FadeIn(
  { delay = 0, duration = DURATION.base, children, ...rest },
  ref,
) {
  const enabled = useMotionEnabled();

  if (!enabled) {
    return (
      <motion.div ref={ref} {...rest}>
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: SLIDE_Y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration, ease: EASE_OUT, delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
});

/* ──────────────────────────────────────────────────────────────────────────
 * Stagger + StaggerItem — container that cascades its children in
 * ────────────────────────────────────────────────────────────────────────── */

const staggerContainerVariants: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.04, delayChildren: 0.02 },
  },
};

const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: SLIDE_Y },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.base, ease: EASE_OUT },
  },
};

export interface StaggerProps extends HTMLMotionProps<'div'> {
  children?: ReactNode;
}

/**
 * Container that cascades its <StaggerItem> children in with a ~0.04s stagger.
 * Wrap each animated child in <StaggerItem>. No-ops under reduced motion.
 */
export const Stagger = forwardRef<HTMLDivElement, StaggerProps>(function Stagger(
  { children, ...rest },
  ref,
) {
  const enabled = useMotionEnabled();

  if (!enabled) {
    return (
      <motion.div ref={ref} {...rest}>
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      variants={staggerContainerVariants}
      initial="hidden"
      animate="show"
      {...rest}
    >
      {children}
    </motion.div>
  );
});

export interface StaggerItemProps extends HTMLMotionProps<'div'> {
  children?: ReactNode;
}

/**
 * A single child of <Stagger>. Fades + slides up 8px as part of the cascade.
 * Renders static when motion is disabled.
 */
export const StaggerItem = forwardRef<HTMLDivElement, StaggerItemProps>(
  function StaggerItem({ children, ...rest }, ref) {
    const enabled = useMotionEnabled();

    if (!enabled) {
      return (
        <motion.div ref={ref} {...rest}>
          {children}
        </motion.div>
      );
    }

    return (
      <motion.div ref={ref} variants={staggerItemVariants} {...rest}>
        {children}
      </motion.div>
    );
  },
);

/* ──────────────────────────────────────────────────────────────────────────
 * HoverLift — card hover lift wrapper
 * ────────────────────────────────────────────────────────────────────────── */

export interface HoverLiftProps extends HTMLMotionProps<'div'> {
  /** Vertical lift in px on hover. Defaults to -3. */
  lift?: number;
  /** Scale on hover. Defaults to 1.02. */
  scale?: number;
  children?: ReactNode;
}

/**
 * Wraps a card so it lifts (-2..-3px), scales slightly (~1.02), and gains a soft
 * shadow on hover. No-ops under reduced motion. Add your own bg/border/rounded
 * classes via className.
 */
export const HoverLift = forwardRef<HTMLDivElement, HoverLiftProps>(
  function HoverLift({ lift = -3, scale = 1.02, children, ...rest }, ref) {
    const enabled = useMotionEnabled();

    if (!enabled) {
      return (
        <motion.div ref={ref} {...rest}>
          {children}
        </motion.div>
      );
    }

    return (
      <motion.div
        ref={ref}
        whileHover={{
          y: lift,
          scale,
          boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.5)',
        }}
        transition={{ duration: DURATION.fast, ease: EASE_OUT }}
        {...rest}
      >
        {children}
      </motion.div>
    );
  },
);

/* ──────────────────────────────────────────────────────────────────────────
 * Pressable — hover+tap scale wrapper (renders a div)
 * ────────────────────────────────────────────────────────────────────────── */

export interface PressableProps extends HTMLMotionProps<'div'> {
  /** Hover scale. Defaults to 1.02. */
  hoverScale?: number;
  /** Tap/active scale. Defaults to 0.97. */
  tapScale?: number;
  children?: ReactNode;
}

/**
 * Generic hover/tap-scale wrapper that renders a <div>. Use this to make a
 * non-button element (e.g. a Link or an icon) feel pressable. For real buttons
 * prefer <MotionButton>. No-ops under reduced motion.
 */
export const Pressable = forwardRef<HTMLDivElement, PressableProps>(
  function Pressable(
    { hoverScale = 1.02, tapScale = 0.97, children, ...rest },
    ref,
  ) {
    const enabled = useMotionEnabled();

    if (!enabled) {
      return (
        <motion.div ref={ref} {...rest}>
          {children}
        </motion.div>
      );
    }

    return (
      <motion.div
        ref={ref}
        whileHover={{ scale: hoverScale }}
        whileTap={{ scale: tapScale }}
        transition={TAP_SPRING}
        {...rest}
      >
        {children}
      </motion.div>
    );
  },
);

/* ──────────────────────────────────────────────────────────────────────────
 * MotionButton — a <button> with hover+tap scale
 * ────────────────────────────────────────────────────────────────────────── */

export interface MotionButtonProps extends HTMLMotionProps<'button'> {
  /** Hover scale. Defaults to 1.02. */
  hoverScale?: number;
  /** Tap/active scale. Defaults to 0.97. */
  tapScale?: number;
}

/**
 * A real <button> that scales on hover (~1.02) and tap (~0.97) with a light
 * spring. No-ops under reduced motion. All native button props pass through.
 */
export const MotionButton = forwardRef<HTMLButtonElement, MotionButtonProps>(
  function MotionButton(
    { hoverScale = 1.02, tapScale = 0.97, children, ...rest },
    ref,
  ) {
    const enabled = useMotionEnabled();

    if (!enabled) {
      return (
        <motion.button ref={ref} {...rest}>
          {children}
        </motion.button>
      );
    }

    return (
      <motion.button
        ref={ref}
        whileHover={{ scale: hoverScale }}
        whileTap={{ scale: tapScale }}
        transition={TAP_SPRING}
        {...rest}
      >
        {children}
      </motion.button>
    );
  },
);

/* ──────────────────────────────────────────────────────────────────────────
 * PageTransition — used by app-router template.tsx
 * ────────────────────────────────────────────────────────────────────────── */

export interface PageTransitionProps extends HTMLMotionProps<'div'> {
  children?: ReactNode;
}

/**
 * Wraps a route's content so each navigation fades + slides in 8px (base
 * duration). Intended for use inside a Next app-router template.tsx, which
 * remounts on every navigation. No-ops under reduced motion.
 *
 * Renders a full-height flex column so full-height pages (e.g. the chat
 * two-pane layout) keep their min-h-0 flex chain intact.
 */
export const PageTransition = forwardRef<HTMLDivElement, PageTransitionProps>(
  function PageTransition({ children, className, ...rest }, ref) {
    const enabled = useMotionEnabled();
    const layoutClass = `flex flex-col flex-1 min-h-0${className ? ` ${className}` : ''}`;

    if (!enabled) {
      return (
        <motion.div ref={ref} className={layoutClass} {...rest}>
          {children}
        </motion.div>
      );
    }

    // Opacity-only on purpose: a y-transform here would wrap the ENTIRE page
    // subtree every navigation (extra cost on heavy pages, and a transform
    // creates a containing block that breaks position:fixed/sticky children).
    return (
      <motion.div
        ref={ref}
        className={layoutClass}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT }}
        {...rest}
      >
        {children}
      </motion.div>
    );
  },
);
